import * as THREE from "three";

import type { AudioFeatures } from "../audio/types";
import { FlowFormParticleSystemGL } from "../engine-webgl/FlowFormParticleSystemGL";
import type { AltSceneApi, CameraPersonality } from "./altScenes";
import {
  particleCountForTier,
  type BioclastParams,
  type FlowFormLookId,
} from "./scenes/bioclast";

const FLOW_FORM_BILLBOARD_CAP = 240_000;

export class FlowFormGPUScene implements AltSceneApi {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(48, 1, 0.1, 500);
  readonly backdrop = { r: 2, g: 7, b: 16, alpha: 0.48, blurPx: 38 };
  readonly cameraPersonality: CameraPersonality = "orbit";
  beatLevel = 0;

  private particles: FlowFormParticleSystemGL | null = null;
  private particleCount = 0;
  private requestedParticleCount = 60_000;
  private viewportHeight = 800;
  private pixelRatio = 1;
  private look: FlowFormLookId = "cellular-matter";
  private bass = 0;
  private mid = 0;
  private treble = 0;
  private level = 0;
  private onset = 0;
  private onsetArmed = true;
  private pointSize = 0.04;
  private alpha = 0.45;
  private baseRadius = 7.2;
  private flowSpeed = 1;
  private intensity = 1;
  private saturation = 1;
  private hue = 148;

  constructor(width: number, height: number) {
    this.camera.position.set(0, 0, 44);
    this.camera.lookAt(0, 0, 0);
    this.setSize(width, height);
  }

  setSize(width: number, height: number): void {
    this.camera.aspect = width / Math.max(1, height);
    this.camera.updateProjectionMatrix();
    this.viewportHeight = Math.max(1, height);
    this.particles?.setViewportHeight(this.viewportHeight * this.pixelRatio);
  }

  update(dt: number, params: BioclastParams, features: AudioFeatures): void {
    this.requestedParticleCount = Math.min(
      FLOW_FORM_BILLBOARD_CAP,
      particleCountForTier(params.qualityTier),
    );
    this.look = params.flowFormLook;
    this.beatLevel = features.beat > this.beatLevel
      ? features.beat
      : this.beatLevel * Math.exp(-dt * 5.5);
    if (features.beat < 0.2) this.onsetArmed = true;
    this.onset = this.onsetArmed && features.beat >= 0.65 ? 1 : 0;
    if (this.onset > 0) this.onsetArmed = false;
    this.bass = THREE.MathUtils.clamp(features.bass * params.bassGravity, 0, 1.5);
    this.mid = THREE.MathUtils.clamp(features.mid * params.midCurl, 0, 1.5);
    this.treble = THREE.MathUtils.clamp(features.treble * params.trebleShimmer, 0, 1.5);
    this.level = THREE.MathUtils.clamp(features.level * params.intensity, 0, 1.5);
    this.baseRadius = 7.2 * THREE.MathUtils.clamp(params.fieldTightness, 0.7, 1.35);
    this.flowSpeed = params.flowSpeed;
    this.intensity = params.intensity;
    this.saturation = params.saturation;
    this.hue = params.paletteHue;
    const densityScale = Math.pow(60_000 / Math.max(1, this.requestedParticleCount), 0.1);
    const restrainedLookGain = this.look === "deep-field"
      ? 1.25
      : this.look === "ramsey-flow" ? 1.4 : 1;
    this.pointSize = THREE.MathUtils.clamp(
      (0.02 + params.starSize * 0.033) * densityScale,
      0.011,
      0.058,
    );
    const densityAlpha = Math.sqrt(60_000 / Math.max(15_000, this.requestedParticleCount));
    this.alpha = THREE.MathUtils.clamp(
      params.opacity * 0.18 * densityAlpha * restrainedLookGain,
      0.045,
      0.38,
    );
    this.syncParticles();

    const hue = ((params.paletteHue % 360) + 360) % 360;
    const tint = new THREE.Color().setHSL(hue / 360, 0.72, 0.2);
    this.backdrop.r = Math.round(2 + tint.r * 24 + this.bass * 8);
    this.backdrop.g = Math.round(4 + tint.g * 28 + this.mid * 8);
    this.backdrop.b = Math.round(12 + tint.b * 36 + this.treble * 10);
    this.backdrop.alpha = 0.36 + THREE.MathUtils.clamp(this.level * 0.16, 0, 0.28);
    this.backdrop.blurPx = 34 + this.level * 12;
  }

  prerender(renderer: THREE.WebGLRenderer, dt: number, time: number): void {
    this.pixelRatio = renderer.getPixelRatio();
    if (!this.particles || this.particleCount !== this.requestedParticleCount) {
      this.rebuildParticles(renderer, this.requestedParticleCount);
    }
    this.syncParticles();
    this.particles?.step(dt, time);
    this.onset = 0;
  }

  getDiagnostics(): {
    backend: string;
    particleCount: number;
    look: FlowFormLookId;
    spatial?: ReturnType<FlowFormParticleSystemGL["getSpatialDiagnostics"]>;
  } {
    return {
      backend: "webgl-gpgpu-flow-form",
      particleCount: this.particleCount,
      look: this.look,
      spatial: this.particles?.getSpatialDiagnostics(),
    };
  }

  dispose(): void {
    if (this.particles) {
      this.scene.remove(this.particles.mesh);
      this.particles.dispose();
      this.particles = null;
    }
    this.particleCount = 0;
    this.scene.clear();
  }

  private rebuildParticles(renderer: THREE.WebGLRenderer, count: number): void {
    if (this.particles) {
      this.scene.remove(this.particles.mesh);
      this.particles.dispose();
    }
    this.particles = new FlowFormParticleSystemGL(renderer, count);
    this.particleCount = count;
    this.syncParticles();
    this.particles.init();
    this.scene.add(this.particles.mesh);
  }

  private syncParticles(): void {
    if (!this.particles) return;
    this.particles.setLook(this.look);
    this.particles.setBaseRadius(this.baseRadius);
    this.particles.setViewportHeight(this.viewportHeight * this.pixelRatio);
    this.particles.setPointSize(this.pointSize);
    this.particles.setAlpha(this.alpha);
    this.particles.setAudio(this.bass, this.mid, this.treble, this.level, this.onset);
    this.particles.setVisual(this.flowSpeed, this.intensity, this.saturation, this.hue);
  }
}